import { PurchaseQuantityDialog } from './PurchaseQuantityDialog';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { Tag } from '@/components/Tag';
import { Icon } from '@/components/Icon';
import { Alert, ErrorState, Skeleton } from '@/components/States';
import { legacyHref } from '@/shell/nav';
import { useSession } from '@/stores/session';
import { date, dateTime, daysUntil, money, num, qty } from '@/lib/format';
import { generatePurchaseOrderPdf, useResource, useSuppliers, useWarehouses } from './api';
import { auditAction, purchaseStatus } from './status';
import { Arrive, Progress, packText, poTotals } from './PurchaseCards';
import { PurchaseConfirmDialog } from './PurchaseConfirmDialog';
import { PurchaseReceiveDialog } from './PurchaseReceiveDialog';
import { PurchaseAdjustmentDialog } from './PurchaseAdjustmentDialog';
import { PurchaseCreateDialog } from './PurchaseCreateDialog';
import type { Num, PurchaseAuditLog, PurchaseOrderDetail as TPO, PurchaseReceiptAdjustment, PurchaseReceiptTx, Supplier } from './types';
import s from './PurchaseOrderDetail.module.css';

/** 同一次「转入库」写出的多条流水挨得很近：同一操作人、相隔 10 秒内算一批 */
function batches(txs: PurchaseReceiptTx[]) {
  const asc = [...txs].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  const out: { at: string; by?: string; rows: PurchaseReceiptTx[] }[] = [];
  for (const t of asc) {
    const last = out[out.length - 1];
    const prev = last?.rows[last.rows.length - 1];
    if (last && prev && last.by === t.createdBy?.name && Math.abs(new Date(t.createdAt).getTime() - new Date(prev.createdAt).getTime()) < 10000) last.rows.push(t);
    else out.push({ at: t.createdAt, by: t.createdBy?.name, rows: [t] });
  }
  return out.reverse().map((b, i, arr) => ({ ...b, no: arr.length - i }));
}

function settlementText(sp: Supplier) {
  const parts = [sp.isMonthlySettlement ? '月结' : '非月结'];
  if (sp.settlementDay) parts.push(`每月 ${sp.settlementDay} 日结算`);
  if (sp.paymentTermDays != null) parts.push(`账期 ${sp.paymentTermDays} 天`);
  return parts.join(' · ');
}

type PurchaseSettlement = {
  purchaseOrderId: string;
  payableAmount: Num;
  paidAmount: Num;
  unpaidAmount: Num;
  invoiceReceivedAmount: Num;
  invoiceBalanceAmount: Num;
};

type ShortageLinks = { items: { productId: string; salesOrderId: string; orderNo: string; status: string }[] };

function invoiceStatus(balance: number) {
  if (balance > 0.004) return { label: '待收票', tone: 'amber' as const };
  if (balance < -0.004) return { label: '超收票', tone: 'blue' as const };
  return { label: '收票完成', tone: 'green' as const };
}

function maskPhone(phone: string) {
  return phone.length >= 7 ? `${phone.slice(0, 3)}****${phone.slice(-4)}` : phone;
}

function auditRecord(a: PurchaseAuditLog) {
  const m = a.metadata as { lineChanges?: unknown[]; previousStatus?: string; receivedQuantity?: number } | null | undefined;
  if (Array.isArray(m?.lineChanges) && m.lineChanges.length) return `明细变更 ${m.lineChanges.length} 条`;
  if (m?.receivedQuantity) return `本次入库 ${qty(m.receivedQuantity)}`;
  if (m?.previousStatus) return `原状态：${purchaseStatus[m.previousStatus as keyof typeof purchaseStatus]?.label || m.previousStatus}`;
  return '';
}

/**
 * 采购单详情：手机整页（深层页，壳层自动隐藏 Tab 栏）、iPad 右栏内嵌、桌面整页。
 * 按容器宽度排版（container-type:inline-size），不按屏幕宽度。
 * 本地开发的入库、关闭剩余和删除由 V2 入口复用 V1 处理器。
 */
export function PurchaseOrderDetail({ id, embedded, onDeleted }: { id: string; embedded?: boolean; onDeleted?: () => void }) {
  const ff = useFormFactor();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const can = useSession((st) => st.can);
  const writable = import.meta.env.VITE_READONLY === '0';
  const [receiveOpen, setReceiveOpen] = useState(() => searchParams.get('action') === 'receive' && import.meta.env.VITE_READONLY === '0' && can('purchase.receive'));
  const [adjustKind, setAdjustKind] = useState<'REJECTION' | 'RETURN' | null>(null);
  const [quantityOpen, setQuantityOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'close' | 'delete' | 'submit' | null>(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const { data: po, loading, error, reload } = useResource<TPO>(`/api/purchase-orders/${encodeURIComponent(id)}`);
  const settlement = useResource<PurchaseSettlement>(can('settlements.supplier.read') ? `/api/v2/purchase-orders/${encodeURIComponent(id)}/settlement` : null);
  const shortageLinks = useResource<ShortageLinks>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/shortage-links`);
  const adjustments = useResource<{ items: PurchaseReceiptAdjustment[] }>(`/api/v2/purchase-orders/${encodeURIComponent(id)}/adjustments`);
  const settled = settlement.data?.purchaseOrderId === id ? settlement.data : undefined;
  const reloadAll = () => { reload(); settlement.reload(); shortageLinks.reload(); adjustments.reload(); };
  const canLoadEditOptions = writable && can('purchase.edit') && po?.status === 'DRAFT';
  const suppliers = useSuppliers(canLoadEditOptions);
  const warehouses = useWarehouses(canLoadEditOptions);

  if (error) return <ErrorState title={error.message === '采购单不存在' ? '采购单不存在' : '采购单加载失败'} message={error.message} detail={`GET /api/purchase-orders/${id}`} onRetry={reload} />;
  if (loading || !po) return <div className={s.loading}><Skeleton /><Skeleton lines={6} /></div>;

  const st = purchaseStatus[po.status] || { label: po.status, tone: 'none' as const };
  const t = poTotals(po.lines);
  const priced = po.lines.some((l) => l.unitCost !== undefined);
  const showMoney = po.totalAmount !== undefined;
  const late = daysUntil(po.expectedArrivalDate);
  const awaiting = po.status === 'ORDERED' || po.status === 'PARTIALLY_RECEIVED';
  const canReceive = writable && awaiting && can('purchase.receive');
  const canReject = writable && awaiting && can('purchase.receive') && t.pending > 0;
  const canReturn = writable && (po.status === 'RECEIVED' || po.status === 'PARTIALLY_RECEIVED') && can('purchase.receive') && t.received > 0;
  const canAdjustQuantity = writable && po.status !== 'DRAFT' && po.status !== 'CANCELLED' && can('purchase.edit');
  const canEditDraft = writable && po.status === 'DRAFT' && can('purchase.edit');
  const canClose = writable && po.status === 'PARTIALLY_RECEIVED' && t.pending > 0 && can('purchase.edit');
  const canDelete = writable && (po.status === 'DRAFT' || po.status === 'ORDERED') && t.received <= 0 && can('purchase.delete');
  const canSubmit = writable && po.status === 'DRAFT' && can('purchase.edit');
  const canPdf = Boolean(po.pdfFileUrl || (writable && can('purchase.pdf.write')));
  const canViewTransactions = can('inventory.transactions.read');
  const showMore = ff === 'phone' || canPdf || canViewTransactions || canDelete || canAdjustQuantity || (embedded && (canReceive || canClose || canSubmit || canEditDraft));
  const wide = !embedded && ff !== 'phone';
  const bs = batches((po.inventoryTransactions || []).filter((event) => event.type === 'PURCHASE_IN'));
  const logs = po.auditLogs || [];
  const currentOrder = po;
  const receiptState = settled ? invoiceStatus(num(settled.invoiceBalanceAmount)) : null;
  const linkedOrders = [...new Map((shortageLinks.data?.items || []).map((item) => [item.salesOrderId, item])).values()];
  const linkPanel = <section className={s.panel}><h3>缺口关联</h3><div className={s.shortageLinks}>
    {shortageLinks.loading ? '正在读取关联订单…' : shortageLinks.error ? <span>关联订单加载失败 <button type="button" onClick={shortageLinks.reload}>重试</button></span> : linkedOrders.length ? <>这张采购单在创建时关联了 {linkedOrders.length} 张缺口订单：<span>{linkedOrders.map((item) => <Link key={item.salesOrderId} to={`/orders/${item.salesOrderId}`}>{item.orderNo}</Link>)}</span><small>关联记录保留创建时的来源；当前可发货状态以订单和库存实时数据为准。</small></> : '这张采购单未记录来源缺口订单。'}</div></section>;

  async function openPdf() {
    setMoreOpen(false);
    setPdfError('');
    if (currentOrder.pdfFileUrl) {
      window.open(currentOrder.pdfFileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    setPdfBusy(true);
    try {
      const result = await generatePurchaseOrderPdf(currentOrder.id);
      if (!result.pdfFileUrl) throw new Error('PDF 已生成，但未返回文件地址');
      window.open(result.pdfFileUrl, '_blank', 'noopener,noreferrer');
      reload();
    } catch (cause) {
      setPdfError(cause instanceof Error ? cause.message : 'PDF 生成失败');
    } finally {
      setPdfBusy(false);
    }
  }

  const main = (
    <>
      {pdfError && <Alert tone="danger" title="采购单 PDF 操作失败">{pdfError}</Alert>}
      {po.status === 'CANCELLED' && <Alert tone="info" title="采购单已取消">不再入库；已入库的部分（如有）保留在库存里。</Alert>}
      {ff !== 'desktop' && awaiting && late !== null && late < 0 && (
        <Alert tone="danger" icon="clock" title={`预计 ${date(po.expectedArrivalDate)} 到货，已逾期 ${-late} 天`}>
          还有 {qty(t.pending)} 个没到。催货请联系供应商{po.supplier.contact ? ` ${po.supplier.contact}` : ''}；到货后在「转入库」核对数量。
        </Alert>
      )}
      {ff !== 'desktop' && po.status === 'PARTIALLY_RECEIVED' && (late === null || late >= 0) && (
        <Alert tone="info" title={`剩余 ${qty(t.pending)} 个未到货`}>供应商交不了货时，可以「终止剩余入库」把采购数量收口到已入库数量。</Alert>
      )}

      {ff === 'phone' && po.status !== 'DRAFT' && po.status !== 'CANCELLED' && <div className={s.progWrap}><Progress received={t.received} ordered={t.ordered} label /></div>}
      <div className={`${s.kpis} ${ff === 'tablet' ? (embedded ? s.tabletKpis : s.focusTabletKpis) : ''}`}>
        <div><span>采购数量</span><b className="tnum">{qty(t.ordered)}</b></div>
        <div><span>已入库</span><b className={`tnum ${s.hl}`}>{qty(t.received)}</b></div>
        <div><span>未入库</span><b className={`tnum ${t.pending > 0 ? s.warn : ''}`}>{qty(t.pending)}</b></div>
        {ff !== 'phone' && !(ff === 'tablet' && embedded) && t.receivedValue !== undefined && <div><span>已入库货值</span><b className="tnum">{money(t.receivedValue)}</b></div>}
        {ff !== 'phone' && t.pendingValue !== undefined && <div><span>未入库货值</span><b className="tnum">{money(t.pendingValue)}</b></div>}
      </div>
      {ff === 'phone' && linkPanel}

      <section className={s.panel}>
        <h3>采购明细<small>{po.lines.length} 项</small></h3>
        <div className={s.tblWrap}>
          <table className={s.tbl}>
            <thead>
              <tr>
                <th>产品</th>
                <th className={s.r}>采购</th>
                <th className={s.r}>已入库</th>
                <th className={s.r}>未入库</th>
                {priced && <th className={`${s.r} ${s.hideNarrow}`}>单价</th>}
                {priced && <th className={`${s.r} ${s.hideNarrow}`}>金额</th>}
                {priced && <th className={s.hideNarrow}>报价依据</th>}
              </tr>
            </thead>
            <tbody>
              {po.lines.map((l) => {
                const p = Math.max(num(l.quantity) - num(l.receivedQuantity), 0);
                const quote = l.supplierQuote;
                const changed = quote && Math.abs(num(quote.quotePrice) - num(l.unitCost)) > 0.001;
                return (
                  <tr key={l.id}>
                    <td>
                      <div className={s.pn}>{l.product.name}</div>
                      <small className={s.pm}>
                        <span className="tnum">{l.product.code}</span>
                        {packText(l) && <> · {packText(l)}</>}
                        {l.unitCost !== undefined && <span className={s.onlyNarrow}> · 单价 {money(l.unitCost)}</span>}
                      </small>
                      {l.note && <small className={s.pm}>{l.note}</small>}
                    </td>
                    <td className={s.r}>{qty(l.quantity)}</td>
                    <td className={s.r}>{qty(l.receivedQuantity)}</td>
                    <td className={`${s.r} ${p > 0 ? s.warn : ''}`}>{qty(p)}</td>
                    {priced && <td className={`${s.r} ${s.hideNarrow}`}>{l.unitCost !== undefined ? money(l.unitCost) : '—'}</td>}
                    {priced && <td className={`${s.r} ${s.hideNarrow}`}>{l.amount !== undefined ? money(l.amount) : '—'}</td>}
                    {priced && (
                      <td className={s.hideNarrow}>
                        {!quote ? <span className="em">手工单价</span> : <>{money(quote.quotePrice)}{changed && <span className={s.adj}>单价已调整</span>}</>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className={s.panel}>
        <h3>入库记录<small>每次入库都会写库存流水</small></h3>
        {bs.length === 0 ? (
          <p className={s.none}>还没有入库记录。到货后在「转入库」核对数量。</p>
        ) : (
          bs.map((b) => {
            const sum = b.rows.reduce((a, r) => a + num(r.quantity), 0);
            return (
              <div key={b.no} className={s.batch}>
                <div className={s.bh}>
                  <b>第 {b.no} 批</b>
                  <span className="tnum em">入库 {qty(sum)}</span>
                  <span className={s.sp} />
                  <span className="tnum em">{dateTime(b.at)}{b.by && ` · ${b.by}`}</span>
                </div>
                <ul>
                  {b.rows.map((r) => (
                    <li key={r.id}>
                      <div>
                        <b>{r.product.name}</b>
                        <small>{r.warehouse.name}{r.unitCost != null && <> · 入库单价 <span className="tnum">{money(r.unitCost)}</span></>}</small>
                        {r.note && <small>{r.note}</small>}
                      </div>
                      <div className={s.bq}>
                        <b className="tnum">{qty(r.quantity)}</b>
                        {r.unitCost != null && <small className="tnum">{money(num(r.quantity) * num(r.unitCost))}</small>}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })
        )}
        {ff === 'desktop' && awaiting && <p className={s.receiptNote}>剩余 {qty(t.pending)} 个未到货{po.expectedArrivalDate ? `。预计 ${date(po.expectedArrivalDate)}；` : '；'}如果供应商无法交付，可以「终止剩余入库」把这张单结案。</p>}
      </section>
      <section className={s.panel}>
        <h3>拒收与退货<small>按实际发生月份列入供应商对账</small></h3>
        {(canReject || canReturn) && <div className={s.adjustActions}>{canReject && <button type="button" onClick={() => setAdjustKind('REJECTION')}>登记到货拒收</button>}{canReturn && <button type="button" onClick={() => setAdjustKind('RETURN')}>登记已入库退货</button>}</div>}
        {adjustments.loading ? <p className={s.none}>正在读取拒收退货记录…</p> : adjustments.error ? <p className={s.none}>读取失败：{adjustments.error.message} <button type="button" onClick={adjustments.reload}>重试</button></p> : adjustments.data?.items.length ? <ul className={s.adjustList}>{adjustments.data.items.map((item) => <li key={item.id}><b>{item.kind === 'RETURN' ? '退货冲减' : '到货拒收'}</b><span>{item.purchaseOrderLine.product.name} · {qty(item.quantity)} 件 · {money(num(item.quantity) * num(item.unitCost))}</span><small>{dateTime(item.createdAt)} · {item.reason}</small></li>)}</ul> : <p className={s.none}>尚无拒收或退货记录。</p>}
      </section>
    </>
  );

  const info = (
    <>
      <section className={s.panel}>
        <h3>基本信息</h3>
        <dl className={s.kv}>
          <div><dt>供应商</dt><dd>{po.supplier.name}</dd></div>
          {(po.supplier.contact || po.supplier.phone) && (
            <div><dt>联系人</dt><dd>{po.supplier.contact}{po.supplier.phone && <> <a className={s.tel} href={`tel:${po.supplier.phone}`}><Icon name="phone" size={14} />{maskPhone(po.supplier.phone)}</a></>}</dd></div>
          )}
          <div><dt>入库仓库</dt><dd>{po.warehouse.name} <span className="em tnum">{po.warehouse.code}</span></dd></div>
          <div><dt>采购日期</dt><dd className="tnum">{date(po.orderDate)}</dd></div>
          <div><dt>预计到货</dt><dd><span className="tnum">{date(po.expectedArrivalDate)}</span> {ff !== 'desktop' && <Arrive po={po} />}</dd></div>
          <div><dt>创建人</dt><dd>{po.createdBy?.name || '—'}</dd></div>
          <div><dt>创建时间</dt><dd className="tnum">{dateTime(po.createdAt)}</dd></div>
          <div><dt>PDF</dt><dd>{po.pdfGeneratedAt ? <>已生成 <span className="tnum em">{dateTime(po.pdfGeneratedAt)}</span></> : <span className="em">未生成</span>}</dd></div>
          {po.note && <div><dt>备注</dt><dd>{po.note}</dd></div>}
        </dl>
      </section>
      {showMoney && (
        <section className={s.panel}>
          <h3>结算</h3>
          <dl className={s.kv}>
            <div><dt>采购总额</dt><dd className={`${s.big} tnum`}>{money(po.totalAmount)}</dd></div>
            {settled && <div><dt>实际入库应付</dt><dd className="tnum">{money(settled.payableAmount)}</dd></div>}
            {ff === 'phone' && t.receivedValue !== undefined && <div><dt>已入库货值</dt><dd className="tnum">{money(t.receivedValue)}</dd></div>}
            {ff === 'phone' && t.pendingValue !== undefined && <div><dt>未入库货值</dt><dd className={`tnum ${t.pendingValue > 0 ? s.warn : ''}`}>{money(t.pendingValue)}</dd></div>}
            {can('settlements.supplier.read') && settled && <>
              {ff === 'phone' ? (
                <div><dt>已付 / 未付</dt><dd className="tnum" title="已付金额只统计已核销到这张采购单的付款">{money(settled.paidAmount)} / <span className={num(settled.unpaidAmount) > 0 ? s.warn : ''}>{money(settled.unpaidAmount)}</span></dd></div>
              ) : <>
                <div><dt>已付款</dt><dd className="tnum" title="已核销到这张采购单的付款">{money(settled.paidAmount)}</dd></div>
                <div><dt>未付款</dt><dd className={`tnum ${num(settled.unpaidAmount) > 0 ? s.warn : ''}`}>{money(settled.unpaidAmount)}</dd></div>
              </>}
              <div><dt>收票</dt><dd>{receiptState && <Tag tone={receiptState.tone} sm>{receiptState.label}</Tag>}</dd></div>
            </>}
            {can('settlements.supplier.read') && (settlement.loading || (!settled && !settlement.error)) && <div><dt>付款/收票</dt><dd className="em">加载中…</dd></div>}
            {can('settlements.supplier.read') && settlement.error && <div><dt>付款/收票</dt><dd className="em">加载失败 <button type="button" onClick={settlement.reload}>重试</button></dd></div>}
            <div><dt>结算方式</dt><dd>{settlementText(po.supplier)}</dd></div>
          </dl>
        </section>
      )}
      {ff !== 'phone' && linkPanel}
      <section className={s.panel}>
        <h3>操作记录<small>{logs.length} 条</small></h3>
        {logs.length === 0 ? (
          <p className={s.none}>没有操作记录。</p>
        ) : (
          <ol className={s.log}>
            {logs.map((a) => (
              <li key={a.id}>
                <b>{auditAction[a.action] || a.action}</b>
                <span className="em tnum">{dateTime(a.createdAt)}{a.user && ` · ${a.user.name}`}</span>
                {(a.summary || auditRecord(a)) && <p>{[a.summary, auditRecord(a)].filter(Boolean).join(' · ')}</p>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </>
  );

  return (
    <div className={`${s.page} ${embedded ? s.embedded : ''} ${ff === 'phone' ? s.phone : ''} ${ff === 'desktop' ? s.desktop : ''}`}>
      <header className={s.head}>
        {!embedded && (
          <button type="button" className={s.back} onClick={() => (history.length > 1 ? nav(-1) : nav('/purchase-orders'))} aria-label="返回"><Icon name="back" size={20} /></button>
        )}
        <h1 className={ff === 'phone' ? undefined : 'tnum'}>{ff === 'phone' ? '采购单详情' : po.purchaseNo}</h1>
        {ff !== 'phone' && <Tag tone={st.tone}>{st.label}</Tag>}
        <span className={s.sp} />
        {embedded && ff !== 'tablet' && <Link className={s.open} to={`/purchase-orders/${po.id}`}>全屏查看</Link>}
        {ff !== 'phone' && canAdjustQuantity && <button type="button" className={s.closeRemaining} onClick={() => setQuantityOpen(true)}>调整采购数量</button>}
        {ff !== 'phone' && canEditDraft && <button type="button" className={s.closeRemaining} onClick={() => setEditOpen(true)}>编辑草稿</button>}
        {ff === 'tablet' && !embedded && canPdf && <button type="button" className={s.pdf} disabled={pdfBusy} onClick={() => void openPdf()}><Icon name="file" size={14} />{pdfBusy ? '正在生成…' : '采购单 PDF'}</button>}
        {ff !== 'phone' && canClose && !(embedded && ff === 'tablet') && <button type="button" className={s.closeRemaining} onClick={() => setConfirmAction('close')}>终止剩余入库</button>}
        {embedded && ff === 'tablet' && canDelete && <button type="button" className={s.delete} onClick={() => setConfirmAction('delete')}>删除</button>}
        {ff !== 'phone' && canSubmit && <button type="button" className={s.receive} onClick={() => setConfirmAction('submit')}>确认下单</button>}
        {ff !== 'phone' && canReceive && <button type="button" className={s.receive} onClick={() => setReceiveOpen(true)}><Icon name="return" size={14} />转入库</button>}
        {ff === 'phone' && canPdf && <button type="button" className={s.moreButton} aria-label="分享采购单 PDF" onClick={() => void openPdf()}><Icon name="send" size={18} /></button>}
        {showMore && <div className={`${s.menuWrap} ${ff === 'phone' ? s.phoneMenu : ''}`}>
          <button type="button" className={s.moreButton} onClick={() => setMoreOpen((open) => !open)} aria-label="更多操作" aria-expanded={moreOpen}><Icon name="more" size={18} /></button>
          {moreOpen && <>
            <button type="button" className={s.menuScrim} aria-label="关闭更多操作" onClick={() => setMoreOpen(false)} />
            <div className={`${s.menu} ${ff === 'tablet' ? s.tabletMenu : ''}`} role="menu">
              {ff === 'phone' && <div className={s.menuTitle}>{po.purchaseNo} · {st.label} · {po.supplier.name}</div>}
              {ff === 'tablet' && <div className={s.tabletMenuTitle}>{po.purchaseNo} · {st.label}</div>}
              {canPdf && (ff !== 'tablet' || embedded) && <button type="button" role="menuitem" disabled={pdfBusy} onClick={() => void openPdf()}>{pdfBusy ? '正在生成 PDF…' : po.pdfFileUrl ? '打开采购单 PDF' : '生成采购单 PDF'}</button>}
              {canViewTransactions && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); nav(`/inventory/transactions?q=${encodeURIComponent(po.purchaseNo)}`); }}>查看这张单的入库流水</button>}
              {ff === 'tablet' && <Link className={s.tabletSupplier} role="menuitem" to={`/suppliers/${po.supplier.id}`}><span>查看供应商</span><small>{po.supplier.name}</small></Link>}
              {canAdjustQuantity && <button type="button" role="menuitem" onClick={() => { setMoreOpen(false); setQuantityOpen(true); }}>调整采购数量</button>}
              {canEditDraft && <button type="button" className={s.narrowAction} role="menuitem" onClick={() => { setMoreOpen(false); setEditOpen(true); }}>编辑草稿</button>}
              {canReceive && ff === 'desktop' && <button type="button" className={s.narrowAction} role="menuitem" onClick={() => { setMoreOpen(false); setReceiveOpen(true); }}>转入库</button>}
              {canClose && <button type="button" className={`${s.narrowAction} ${ff === 'tablet' ? s.tabletCloseAction : ''}`} role="menuitem" onClick={() => { setMoreOpen(false); setConfirmAction('close'); }}>终止剩余入库</button>}
              {canSubmit && <button type="button" className={s.narrowAction} role="menuitem" onClick={() => { setMoreOpen(false); setConfirmAction('submit'); }}>确认下单</button>}
              {canDelete && !(embedded && ff === 'tablet') && <button type="button" className={s.dangerAction} role="menuitem" onClick={() => { setMoreOpen(false); setConfirmAction('delete'); }}>删除采购单</button>}
              {ff === 'tablet' && can('purchase.delete') && !canDelete && <button type="button" className={s.dangerAction} role="menuitem" disabled>删除采购单<small>部分入库的单不能删除，只能终止剩余入库</small></button>}
              {embedded && ff === 'tablet' && <Link role="menuitem" to={`/purchase-orders/${po.id}`}>全屏查看</Link>}
              {ff === 'tablet' && embedded && <a role="menuitem" href={legacyHref(`/purchase-orders/${po.id}`)}>在老版中操作</a>}
              {ff === 'phone' && !canDelete && can('purchase.delete') && t.received > 0 && <button type="button" className={s.dangerAction} role="menuitem" disabled>删除采购单<small>已入库，不能直接删除；部分入库可终止剩余入库</small></button>}
              {ff === 'phone' && <button type="button" className={s.cancelMenu} role="menuitem" onClick={() => setMoreOpen(false)}>取消</button>}
            </div>
          </>}
        </div>}
        {ff !== 'phone' && ff !== 'tablet' && <a className={s.legacy} href={legacyHref(`/purchase-orders/${po.id}`)} aria-label="在老版中操作" title="打开 V1 采购单详情">
          <Icon name="return" size={14} /><span>在老版中操作</span>
        </a>}
      </header>
      <div className={s.scroll}>
        {ff === 'phone' && <div className={s.mobileSummary}>
          <div className={s.mobileSummaryHead}><b className="tnum">{po.purchaseNo}</b><Tag tone={st.tone} sm>{st.label}</Tag></div>
          <p>{po.supplier.name} · {po.warehouse.name}{po.createdBy && ` · ${po.createdBy.name}创建`}</p>
          <strong>已入 <span className="tnum">{qty(t.received)} / {qty(t.ordered)}</span></strong>
          <small>{po.orderDate && `${date(po.orderDate)} 下单`}{po.expectedArrivalDate && ` · 预计 ${date(po.expectedArrivalDate)} 到货`}</small>
        </div>}
        {ff !== 'phone' && <p className={s.sub}>
          {po.supplier.name} · {po.warehouse.name}{po.createdBy && ` · ${po.createdBy.name}创建`}
          {po.orderDate && <> · <span className="tnum">{date(po.orderDate)}</span> 下单</>}
          {po.expectedArrivalDate && <> · 预计 <span className="tnum">{date(po.expectedArrivalDate)}</span> 到货</>}
        </p>}
        {wide ? (
          <div className={s.grid}><div className={s.col}>{main}</div><aside className={s.col}>{info}</aside></div>
        ) : (
          <div className={s.col}>{main}{info}</div>
        )}
      </div>
      {ff === 'phone' && <div className={s.mobileFoot}>
        <button type="button" className={s.mobileMore} onClick={() => setMoreOpen(true)} aria-label="更多操作"><Icon name="more" size={20} /></button>
        {canClose && <button type="button" className={s.mobileSecondary} onClick={() => setConfirmAction('close')}>终止剩余</button>}
        {canAdjustQuantity && <button type="button" className={s.mobileSecondary} onClick={() => setQuantityOpen(true)}>调整数量</button>}
        {canEditDraft && <button type="button" className={s.mobileSecondary} onClick={() => setEditOpen(true)}>编辑草稿</button>}
        {canSubmit && <button type="button" className={s.mobilePrimary} onClick={() => setConfirmAction('submit')}>确认下单</button>}
        {canReceive && <button type="button" className={s.mobilePrimary} onClick={() => setReceiveOpen(true)}>{po.status === 'PARTIALLY_RECEIVED' ? '继续入库' : '转入库'}</button>}
        {!canReceive && !canSubmit && !canClose && !canEditDraft && <a className={s.mobilePrimary} href={legacyHref(`/purchase-orders/${po.id}`)}>在老版中操作</a>}
      </div>}
      {canReceive && receiveOpen && <PurchaseReceiveDialog order={po} canReadInventory={can('inventory.read')} onClose={() => setReceiveOpen(false)} onSuccess={reloadAll} />}
      {adjustKind && <PurchaseAdjustmentDialog order={po} initialKind={adjustKind} onClose={() => setAdjustKind(null)} onSuccess={reloadAll} />}
      {quantityOpen && <PurchaseQuantityDialog order={po} onClose={() => setQuantityOpen(false)} onDone={reloadAll} />}
      {editOpen && <PurchaseCreateDialog initialOrder={po} suppliers={suppliers} warehouses={warehouses} onClose={() => setEditOpen(false)} onCreated={reloadAll} />}
      {confirmAction && <PurchaseConfirmDialog order={po} action={confirmAction} onClose={() => setConfirmAction(null)} onDone={() => {
        setConfirmAction(null);
        if (confirmAction === 'delete') {
          if (onDeleted) onDeleted();
          else nav('/purchase-orders');
        } else reloadAll();
      }} />}
    </div>
  );
}
